<template>
  <div class="sketch-corner-property">
    <n-collapse :default-expanded-names="defaultExpanded" class="pm-collapse">
      <!-- //------ 草图圆角实体面板 开始------ -->
      <n-collapse-item title="信息" name="information">
        <div class="info-panel" data-testid="sketch-corner-info-panel">
          <n-text class="info-text">{{ instructionText }}</n-text>
        </div>
      </n-collapse-item>

      <n-collapse-item :title="entitiesPanelTitle" name="entities">
        <div class="entities-panel" data-testid="sketch-corner-entities-panel">
          <SelectionListFrame
            class="entities-list"
            :class="{ 'selection-list-frame--active': true }"
            :items="selectedItems"
            :active-index="activeSelectionIndex"
            :item-key-field="'id'"
            :item-label-field="'label'"
            :visible-rows="2"
            :context-menu-enabled="true"
            :show-delete-all-action="true"
            :show-delete-action="true"
            :empty-text="entitiesEmptyText"
            delete-label="删除"
            delete-all-label="删除所有"
            @delete-item="handleDeleteSelectionItem"
            @delete-all="handleDeleteAllSelectionItems"
          >
            <template #item="{ item }">
              <div class="entity-main">
                <n-text>{{ resolveSelectionItemLabel(item) }}</n-text>
              </div>
            </template>
          </SelectionListFrame>
        </div>
      </n-collapse-item>
      <!-- //------ 草图圆角实体面板 结束------ -->

      <n-collapse-item :title="parametersPanelTitle" name="parameters">
        <div v-if="mode === SketchCornerMode.Fillet" class="param-grid">
          <div class="param-row">
            <n-text depth="3" class="param-label">半径</n-text>
            <n-input-number
              :value="displayFilletRadius"
              size="small"
              :show-button="true"
              :precision="4"
              :step="1"
              @update:value="handleFilletRadius"
            />
          </div>
          <n-checkbox
            :checked="filletOptions.keepConstrainedCorners"
            @update:checked="handleKeepConstrainedCorners"
          >
            保留受约束角点
          </n-checkbox>
          <n-checkbox
            :checked="filletOptions.dimensionEachFillet"
            @update:checked="handleDimensionEachFillet"
          >
            标注每个圆角的尺寸
          </n-checkbox>
        </div>

        <div v-else class="param-grid">
          <n-radio-group
            :value="chamferDisplayMode"
            size="small"
            @update:value="handleChamferModeChange"
          >
            <div class="radio-list">
              <n-radio value="angle-distance">角度距离</n-radio>
              <n-radio value="distance-distance">距离-距离</n-radio>
            </div>
          </n-radio-group>
          <n-checkbox
            :checked="chamferEqualDistance"
            :disabled="chamferDisplayMode !== SketchChamferUiMode.DistanceDistance"
            @update:checked="handleChamferEqualDistance"
          >
            相等距离
          </n-checkbox>
          <div class="param-row">
            <n-text depth="3" class="param-label">距离1</n-text>
            <n-input-number
              :value="displayChamferDistance1"
              size="small"
              :show-button="true"
              :precision="4"
              :step="1"
              @update:value="handleDistance1"
            />
          </div>
          <div v-if="chamferDisplayMode === SketchChamferUiMode.DistanceDistance && !chamferEqualDistance" class="param-row">
            <n-text depth="3" class="param-label">距离2</n-text>
            <n-input-number
              :value="displayChamferDistance2"
              size="small"
              :show-button="true"
              :precision="4"
              :step="1"
              @update:value="handleDistance2"
            />
          </div>
          <div v-if="chamferDisplayMode === SketchChamferUiMode.AngleDistance" class="param-row">
            <n-text depth="3" class="param-label">角度</n-text>
            <n-input-number
              :value="chamferOptions.angleDeg"
              size="small"
              :show-button="true"
              :precision="8"
              :step="1"
              @update:value="handleAngle"
            />
          </div>
          <!-- //------ 草图倒角命令内撤消 开始------ -->
          <div class="chamfer-undo-row">
            <n-button
              size="small"
              secondary
              class="chamfer-undo-button"
              :disabled="!chamferUndoAvailable"
              @click="handleChamferUndo"
            >
              撤消(U)
            </n-button>
          </div>
          <!-- //------ 草图倒角命令内撤消 结束------ -->
        </div>
      </n-collapse-item>
    </n-collapse>
  </div>
</template>

<script setup lang="ts">
import {computed, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {
  NButton,
  NCheckbox,
  NCollapse,
  NCollapseItem,
  NInputNumber,
  NRadio,
  NRadioGroup,
  NText,
} from 'naive-ui';
import {useViewer} from '@/hooks';
import type {
  PropertyManagerContext,
  SketchChamferOptions,
  SketchFilletOptions,
} from '@/core';
import {
  SketchChamferType,
  SketchCornerMode,
} from '@/core';
import {
  SketchCornerSelectionItemKind,
  type SketchCornerControllerSnapshot,
  type SketchCornerSelectionItem,
  //------ 统一单位转换 开始------
  convertMmToM,
  convertMToMm,
  roundToEightDecimalPlaces,
  //------ 统一单位转换 结束------
} from '@puppycad/cad-core';
import SelectionListFrame from '@/components/editor/SelectionListFrame.vue';

interface SketchCornerSectionProps {
  context: PropertyManagerContext;
}

const props = defineProps<SketchCornerSectionProps>();
const { getViewer, setViewerCallback } = useViewer();

/**
 * 草图倒角参数展示模式枚举。
 */
enum SketchChamferUiMode {
  /** 角度距离模式。 */
  AngleDistance = 'angle-distance',
  /** 距离距离模式。 */
  DistanceDistance = 'distance-distance',
}

/**
 * 草图圆角控制器约定。
 * 属性面板仅依赖这一组公开方法，避免直接耦合控制器内部细节。
 */
interface SketchCornerControllerFacade {
  /**
   * 获取草图圆角/倒角控制器快照。
   * @returns 控制器快照；控制器不可用时返回 null。
   */
  getSnapshot: () => SketchCornerControllerSnapshot | null;
  /**
   * 订阅草图圆角/倒角控制器快照。
   * @param listener 快照变化监听器。
   * @returns 取消订阅函数。
   */
  subscribeSnapshot: (listener: (snapshot: SketchCornerControllerSnapshot) => void) => (() => void) | null;
  /**
   * 获取圆角参数快照。
   * @returns 圆角参数副本。
   */
  getFilletOptionsSnapshot: () => SketchFilletOptions;
  /**
   * 获取倒角参数快照。
   * @returns 倒角参数副本。
   */
  getChamferOptionsSnapshot: () => SketchChamferOptions;
  /**
   * 更新圆角参数。
   * @param options 圆角参数增量。
   */
  setFilletOptions: (options: Partial<SketchFilletOptions>) => void;
  /**
   * 更新倒角参数。
   * @param options 倒角参数增量。
   */
  setChamferOptions: (options: Partial<SketchChamferOptions>) => void;
  /**
   * 重置第一个源实体选择。
   */
  resetSelection: () => void;
  /**
   * 取消当前待确认圆角预览。
   */
  cancelFilletPreview: () => void;
  //------ 草图倒角命令内撤消 开始------
  /**
   * 判断当前倒角命令是否存在可撤消步骤。
   * @returns 存在可撤消倒角步骤时返回 true。
   */
  hasChamferUndo: () => boolean;
  /**
   * 撤消当前倒角命令内最近一次倒角。
   * @returns 撤消成功返回 true。
   */
  undoLastChamfer: () => boolean;
  //------ 草图倒角命令内撤消 结束------
}

/**
 * 默认圆角参数。
 */
const DEFAULT_FILLET_OPTIONS: SketchFilletOptions = {
  radius: convertMmToM(10),
  keepConstrainedCorners: true,
  dimensionEachFillet: false,
};

/**
 * 默认倒角参数。
 */
const DEFAULT_CHAMFER_OPTIONS: SketchChamferOptions = {
  chamferType: SketchChamferType.EqualDistance,
  distance1: convertMmToM(10),
  distance2: convertMmToM(10),
  angleDeg: 45,
};

//------ 草图圆角属性面板逻辑 开始------
const mode = computed(() => props.context.sketchCornerMode ?? SketchCornerMode.Fillet);
const defaultExpanded = ['information', 'entities', 'parameters'];
const selectedItems = ref<SketchCornerSelectionItem[]>([]);
const localFilletOptions = ref<SketchFilletOptions>({ ...DEFAULT_FILLET_OPTIONS });
const localChamferOptions = ref<SketchChamferOptions>({ ...DEFAULT_CHAMFER_OPTIONS });
//------ 草图倒角命令内撤消 开始------
/** 当前倒角命令是否存在可撤消步骤。 */
const chamferUndoAvailable = ref(false);
//------ 草图倒角命令内撤消 结束------
/** 控制器快照订阅取消函数。 */
let disposeSnapshotSubscription: (() => void) | null = null;
/** Viewer 就绪回调取消函数。 */
let disposeViewerReadyCallback: (() => void) | null = null;

const filletOptions = computed(() => localFilletOptions.value);
const chamferOptions = computed(() => localChamferOptions.value);
//------ 统一单位转换 开始------
const displayFilletRadius = computed(() => roundToEightDecimalPlaces(convertMToMm(filletOptions.value.radius)));
const displayChamferDistance1 = computed(() => roundToEightDecimalPlaces(convertMToMm(chamferOptions.value.distance1)));
const displayChamferDistance2 = computed(() => roundToEightDecimalPlaces(convertMToMm(chamferOptions.value.distance2)));
//------ 统一单位转换 结束------
const chamferEqualDistance = computed(() => chamferOptions.value.chamferType === SketchChamferType.EqualDistance);
const chamferDisplayMode = computed(() => (
  chamferOptions.value.chamferType === SketchChamferType.AngleDistance
    ? SketchChamferUiMode.AngleDistance
    : SketchChamferUiMode.DistanceDistance
));

/** 信息面板提示文本。 */
const instructionText = computed(() => (
  mode.value === SketchCornerMode.Fillet
    ? '选取草图顶点或实体以圆角化。'
    : '选取草图顶点或实体以倒角化。'
));

/** 实体选择列表面板标题。 */
const entitiesPanelTitle = computed(() => (
  mode.value === SketchCornerMode.Fillet
    ? '要圆角化的实体(E)'
    : '要倒角化的实体(E)'
));

/** 参数面板标题。 */
const parametersPanelTitle = computed(() => (
  mode.value === SketchCornerMode.Fillet
    ? '圆角参数(P)'
    : '倒角参数(P)'
));

/** 实体选择列表空态提示。 */
const entitiesEmptyText = computed(() => (
  mode.value === SketchCornerMode.Fillet
    ? '请选择要圆角化的实体'
    : '请选择要倒角化的实体'
));

/** 当前列表激活项索引。 */
const activeSelectionIndex = computed(() => (selectedItems.value.length > 0 ? 0 : -1));

/**
 * 获取草图圆角/倒角控制器。
 * @returns 控制器实例；当前不可用时返回 null。
 */
function getSketchCornerController(): SketchCornerControllerFacade | null {
  const renderer = getViewer();
  if (!renderer) {
    return null;
  }
  return {
    getSnapshot: () => renderer.getSketchCornerSnapshot?.() as SketchCornerControllerSnapshot | null,
    subscribeSnapshot: (listener) => {
      const snapshotStream = renderer.observeSketchCornerSnapshot?.() ?? null;
      if (!snapshotStream) {
        return null;
      }
      const subscription = snapshotStream.subscribe(listener);
      if (!subscription) {
        return null;
      }
      return () => {
        subscription.unsubscribe();
      };
    },
    getFilletOptionsSnapshot: () => renderer.getSketchCornerFilletOptions?.() as SketchFilletOptions,
    getChamferOptionsSnapshot: () => renderer.getSketchCornerChamferOptions?.() as SketchChamferOptions,
    setFilletOptions: (options) => {
      renderer.setSketchCornerFilletOptions?.(options);
    },
    setChamferOptions: (options) => {
      renderer.setSketchCornerChamferOptions?.(options);
    },
    resetSelection: () => {
      renderer.resetSketchCornerSelection?.();
    },
    cancelFilletPreview: () => {
      renderer.cancelSketchCornerFilletPreview?.();
    },
    //------ 草图倒角命令内撤消 开始------
    hasChamferUndo: () => Boolean(renderer.hasSketchCornerChamferUndo?.()),
    undoLastChamfer: () => Boolean(renderer.undoLastSketchCornerChamfer?.()),
    //------ 草图倒角命令内撤消 结束------
  };
}

/**
 * 将控制器快照应用到面板选择列表。
 * @param snapshot 控制器快照；控制器未就绪时为空。
 */
function applyControllerSnapshot(snapshot: SketchCornerControllerSnapshot | null): void {
  selectedItems.value = snapshot?.selectedItems.map((item) => ({ ...item })) ?? [];
}

/**
 * 解析实体列表项显示文本。
 * @param item 选择列表插槽项。
 * @returns 可显示的实体名称。
 */
function resolveSelectionItemLabel(item: unknown): string {
  if (!item || typeof item !== 'object') {
    return '';
  }
  const label = (item as Partial<SketchCornerSelectionItem>).label;
  return typeof label === 'string' ? label : '';
}

/**
 * 从控制器同步面板状态。
 * 说明：
 * 1. 统一从控制器读取参数和选择列表快照；
 * 2. 确保工具栏切换模式后，属性面板看到的是最新状态。
 */
function syncCornerPanelState(): void {
  const controller = getSketchCornerController();
  if (!controller) {
    localFilletOptions.value = { ...DEFAULT_FILLET_OPTIONS };
    localChamferOptions.value = { ...DEFAULT_CHAMFER_OPTIONS };
    applyControllerSnapshot(null);
    chamferUndoAvailable.value = false;
    return;
  }

  localFilletOptions.value = controller.getFilletOptionsSnapshot();
  localChamferOptions.value = controller.getChamferOptionsSnapshot();
  applyControllerSnapshot(controller.getSnapshot());
  chamferUndoAvailable.value = controller.hasChamferUndo();
}

/**
 * 绑定控制器快照事件，用于在视口选择或预览状态变化后刷新面板列表。
 */
function bindControllerSnapshot(): void {
  disposeSnapshotSubscription?.();
  disposeSnapshotSubscription = null;

  const controller = getSketchCornerController();
  disposeSnapshotSubscription = controller?.subscribeSnapshot((snapshot) => {
    applyControllerSnapshot(snapshot);
    chamferUndoAvailable.value = controller.hasChamferUndo();
  }) ?? null;
}

/**
 * 更新本地圆角参数，并同步写回控制器。
 * @param patch 圆角参数增量。
 */
function updateFilletOptions(patch: Partial<SketchFilletOptions>): void {
  const controller = getSketchCornerController();
  if (!controller) {
    return;
  }
  controller.setFilletOptions(patch);
  localFilletOptions.value = {
    ...localFilletOptions.value,
    ...patch,
  };
}

/**
 * 更新本地倒角参数，并同步写回控制器。
 * @param patch 倒角参数增量。
 */
function updateChamferOptions(patch: Partial<SketchChamferOptions>): void {
  const controller = getSketchCornerController();
  if (!controller) {
    return;
  }
  controller.setChamferOptions(patch);
  localChamferOptions.value = {
    ...localChamferOptions.value,
    ...patch,
  };
}

/**
 * 删除单个实体选择列表项。
 * @param payload 选择列表删除事件载荷。
 */
function handleDeleteSelectionItem(payload: { item: unknown; index: number }): void {
  const controller = getSketchCornerController();
  if (!controller) {
    return;
  }

  const item = payload.item as Partial<SketchCornerSelectionItem>;
  if (item.kind === SketchCornerSelectionItemKind.FilletPreview) {
    controller.cancelFilletPreview();
  } else {
    controller.resetSelection();
  }
  syncCornerPanelState();
}

/**
 * 删除全部实体选择列表项。
 */
function handleDeleteAllSelectionItems(): void {
  const controller = getSketchCornerController();
  if (!controller) {
    return;
  }

  const hasFilletPreview = selectedItems.value.some((item) => item.kind === SketchCornerSelectionItemKind.FilletPreview);
  if (hasFilletPreview) {
    controller.cancelFilletPreview();
  } else {
    controller.resetSelection();
  }
  syncCornerPanelState();
}

/**
 * 处理圆角半径输入。
 * @param value 输入值。
 */
function handleFilletRadius(value: number | null): void {
  if (value === null || Number.isNaN(value)) {
    return;
  }
  //------ 统一单位转换 开始------
  // 用户输入的毫米值转换为底层所需的米
  updateFilletOptions({ radius: convertMmToM(value) });
  //------ 统一单位转换 结束------
}

/**
 * 处理“保留受约束角点”勾选变化。
 * @param value 勾选值。
 */
function handleKeepConstrainedCorners(value: boolean): void {
  updateFilletOptions({ keepConstrainedCorners: value });
}

/**
 * 处理“标注每个圆角的尺寸”勾选变化。
 * @param value 勾选值。
 */
function handleDimensionEachFillet(value: boolean): void {
  updateFilletOptions({ dimensionEachFillet: value });
}

/**
 * 处理倒角展示模式切换。
 * @param value 目标展示模式。
 */
function handleChamferModeChange(value: SketchChamferUiMode): void {
  if (value === SketchChamferUiMode.AngleDistance) {
    updateChamferOptions({ chamferType: SketchChamferType.AngleDistance });
    return;
  }

  updateChamferOptions({
    chamferType: chamferEqualDistance.value
      ? SketchChamferType.EqualDistance
      : SketchChamferType.DistanceDistance,
  });
}

/**
 * 处理“相等距离”勾选变化。
 * @param value 勾选值。
 */
function handleChamferEqualDistance(value: boolean): void {
  updateChamferOptions({
    chamferType: value
      ? SketchChamferType.EqualDistance
      : SketchChamferType.DistanceDistance,
    distance2: value ? localChamferOptions.value.distance1 : localChamferOptions.value.distance2,
  });
}

/**
 * 处理倒角距离 1 输入。
 * @param value 输入值。
 */
function handleDistance1(value: number | null): void {
  if (value === null || Number.isNaN(value)) {
    return;
  }

  //------ 统一单位转换 开始------
  // 用户输入的毫米值转换为底层所需的米
  updateChamferOptions({
    distance1: convertMmToM(value),
    ...(chamferEqualDistance.value ? { distance2: convertMmToM(value) } : {}),
  });
  //------ 统一单位转换 结束------
}

/**
 * 处理倒角距离 2 输入。
 * @param value 输入值。
 */
function handleDistance2(value: number | null): void {
  if (value === null || Number.isNaN(value)) {
    return;
  }
  //------ 统一单位转换 开始------
  // 用户输入的毫米值转换为底层所需的米
  updateChamferOptions({ distance2: convertMmToM(value) });
  //------ 统一单位转换 结束------
}

/**
 * 处理倒角角度输入。
 * @param value 输入值。
 */
function handleAngle(value: number | null): void {
  if (value === null || Number.isNaN(value)) {
    return;
  }
  updateChamferOptions({ angleDeg: value });
}

//------ 草图倒角命令内撤消 开始------
/**
 * 处理草图倒角命令内撤消。
 */
function handleChamferUndo(): void {
  const controller = getSketchCornerController();
  if (!controller) {
    return;
  }
  /**
   * SolidWorks 的草图倒角“撤消(U)”只回退当前命令内上一笔倒角，
   * 不等同于退出命令或触发全局模型撤销。
   */
  if (!controller.undoLastChamfer()) {
    return;
  }
  syncCornerPanelState();
}
//------ 草图倒角命令内撤消 结束------

onMounted(() => {
  syncCornerPanelState();
  bindControllerSnapshot();
  disposeViewerReadyCallback = setViewerCallback(() => {
    syncCornerPanelState();
    bindControllerSnapshot();
  });
});

onBeforeUnmount(() => {
  disposeSnapshotSubscription?.();
  disposeSnapshotSubscription = null;
  disposeViewerReadyCallback?.();
  disposeViewerReadyCallback = null;
});

watch(mode, () => {
  syncCornerPanelState();
  bindControllerSnapshot();
});
//------ 草图圆角属性面板逻辑 结束------
</script>

<style scoped>
.sketch-corner-property {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pm-collapse :deep(.n-collapse-item__header) {
  padding: 6px 0;
}

.param-grid {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.radio-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.param-row {
  display: grid;
  grid-template-columns: 48px 1fr;
  gap: 8px;
  align-items: center;
}

.param-label {
  font-size: 12px;
}

.info-panel {
  border: 1px solid color-mix(in srgb, var(--n-warning-color, #f0a020) 42%, transparent);
  border-radius: 2px;
  background: color-mix(in srgb, var(--n-warning-color, #f0a020) 18%, transparent);
  padding: 4px 6px;
}

.info-text {
  font-size: 12px;
  line-height: 1.4;
}

.entities-panel {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.entities-list {
  min-height: 42px;
}

.entity-main {
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
}

/* //------ 草图倒角命令内撤消 开始------ */
.chamfer-undo-row {
  display: flex;
  justify-content: center;
  padding-top: 6px;
}

.chamfer-undo-button {
  min-width: 172px;
}
/* //------ 草图倒角命令内撤消 结束------ */

</style>
